import { AdhocReqBody } from "api/types/report";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import { formatDateToZ } from "util/datelib";
import GenericCard from "widgets/cards/generic";
import { SpecialZoomLevel, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

import CircleButton from "widgets/controls/buttons/circle-button";
import { MdFileDownload } from "react-icons/md";
import ReportsSvg from "../../../../../../assets/svg/reports.svg";
import ReportsErrorSvg from "../../../../../../assets/svg/reports-error.svg";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import { useNavigate } from "react-router-dom";
import { ENDPOINTS } from "api/endpoints";
import { reportAdhoc } from "api/report";

interface MyDocumentProps {
  data: any;
}

interface GeneratedFilesUrls {
  pdfUrl?: string;
  excelUrl?: string;
  [key: string]: any;
}

const ViewerPdf: React.FC<MyDocumentProps> = ({ data }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<GeneratedFilesUrls | null>(null);
  const dispatch = useDispatch();
  const { notify } = useNotificationDialog();

  const fromDate = useMemo(
    () =>
      data
        ? formatDateToZ(data.dateRange[0], false, "day", data.timezone)
        : undefined,
    [data]
  );
  const toDate = useMemo(
    () =>
      data
        ? formatDateToZ(data.dateRange[1], true, "day", data.timezone)
        : undefined,
    [data]
  );

  // Generate Adhoc report
  const generateAdhocReport = useCallback(
    async (
      endpoint: string,
      type?: "WATER" | "ELECTRICITY" | "GAS",
      bucket?: "hour",
      sensors?: string[],
      classes?: string[]
    ) => {
      dispatch(setLoading(true));
      try {
        const body: AdhocReqBody = {
          siteId: data?.site || undefined,
          snr: data?.device || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          timezone: data?.timezone || undefined,
          email: data?.email || undefined,
          type: type || undefined,
          bucket: bucket || undefined,
          sensors: sensors || undefined,
          classes: classes || undefined,
          snrs: data?.devices || undefined,
        };
        const cleanedBody = Object.fromEntries(
          Object.entries(body).filter(([_, v]) => v !== undefined)
        ) as AdhocReqBody;

        const { data: res, status } = await reportAdhoc(cleanedBody, endpoint);
        if (status === 200) {
          setFiles(res.url);
        }
      } finally {
        dispatch(setLoading(false));
      }
    },
    [data, dispatch, fromDate, toDate]
  );

  useEffect(() => {
    if (!data) return;
    setFiles(null);

    if (data?.devices && data.devices.length >= 50) {
      notify({
        header: t("dialog.title.report.big"),
        message: t("dialog.description.report.big"),
        okText: t("button.action.route"),
        onCancel: () => {},
        onOk: () => {
          dispatch(setLoading(false));
          navigate(`/admin/reports/view?id=${data.site}`);
        },
      });
    }

    switch (data.reportType) {
      case 1:
        generateAdhocReport(ENDPOINTS.REPORT.RAW);
        break;
      case 2:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "WATER");
        break;
      case 3:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "ELECTRICITY");
        break;
      case 4:
        generateAdhocReport(
          ENDPOINTS.REPORT.DATA.USAGE.AGGREGATE,
          undefined,
          "hour",
          [
            "water-meter",
            "meterreading",
            "forwardticks",
            "otherticks",
            "cumulative-odo",
            "temp-meter",
          ]
        );
        break;
      case 5:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.UPTIME);
        break;
      case 6:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "GAS");
        break;
      case 7:
        generateAdhocReport(ENDPOINTS.REPORT.INCIDENT);
        break;
      case 8:
        generateAdhocReport(
          ENDPOINTS.REPORT.ALERTS,
          undefined,
          undefined,
          undefined,
          [data.site]
        );
        break;
      case 9:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.UPTIME_AUDIT);
        break;
      default:
        //No report type
        break;
    }
  }, [data, dispatch, navigate, notify, generateAdhocReport]);

  // Download PDF file
  const downloadPdf = useCallback(() => {
    if (!files?.pdfUrl) return;
    const link = document.createElement("a");
    link.href = files.pdfUrl;
    link.target = "_blank";
    link.download = "report.pdf";
    link.click();
  }, [files]);

  // Download Excel file
  const downloadExcel = useCallback(() => {
    if (!files?.excelUrl) return;
    const link = document.createElement("a");
    link.href = files.excelUrl;
    link.target = "_blank";
    link.download = "report.xlsx";
    link.click();
  }, [files]);

  return (
    <>
      <GenericCard
        extra="h-full"
        topRight={
          files?.pdfUrl && (
            <div className="flex gap-2">
              {files?.pdfUrl && (
                <CircleButton
                  size="sm"
                  variant="outline"
                  icon={MdFileDownload}
                  label={t("button.download.pdf.full")}
                  onClick={downloadPdf}
                />
              )}
              {files?.excelUrl && (
                <CircleButton
                  size="sm"
                  variant="outline"
                  icon={MdFileDownload}
                  label={t("button.download.excel.full")}
                  onClick={downloadExcel}
                />
              )}
            </div>
          )
        }
      >
        <div className="flex max-h-[670px] w-full">
          {files?.pdfUrl ? (
            <Viewer
              key={files?.pdfUrl}
              fileUrl={files?.pdfUrl}
              defaultScale={SpecialZoomLevel.PageWidth}
              renderError={(error) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "670px",
                    }}
                  >
                    <p style={{ color: "red" }}>
                      {error?.message || "Unknown PDF error"}
                    </p>
                  </div>
                );
              }}
            />
          ) : (
            <>
              <img
                src={ReportsSvg}
                alt="Modal content"
                style={{ width: "100%", margin: 80 }}
              />
            </>
          )}
        </div>
      </GenericCard>
    </>
  );
};

export default ViewerPdf;
